import React, { FC, useEffect } from 'react';
import { Formik } from 'formik';
import { LoadingPage } from 'Shared/components';
import {
  Body,
  CoveragesList,
  ScreenHeader,
  Footer,
  FooterNextButton,
  FooterPreviousButton,
} from 'QuoteApp/components';
import { useFooter } from 'QuoteApp/utils/hooks';
import { useAppContext } from 'QuoteApp/services/providers';
import { RewardsBanner } from 'QuoteApp/components/RewardsBanner';
import {
  buildCoveragesByFieldValue,
  createStackedCoverageMap,
} from 'QuoteApp/utils';
import {
  useSaveCoverages,
  getHealedCoverages,
  getCoverageErrors,
} from 'QuoteApp/services/providers/AppProvider/hooks/useSaveCoverages';
import { useErrorHandler } from 'react-error-boundary';
import { mergeDictionaryIntoCoverages } from 'QuoteApp/services/providers/AppProvider/hooks/useDictionary/utils';
import { useInitializeCoverages } from 'QuoteApp/services/providers/AppProvider/hooks/useInitializeCoverages';

export const Coverages: FC = () => {
  const { previousStep, nextStep } = useFooter(2);
  const {
    config,
    details,
    dictionary,
    refreshRate,
    getRateOnLoad,
    rewardsEnrollmentResponse,
  } = useAppContext();
  const {
    runnable: shouldInitializeCoverages,
    perform: initializeCoverages,
  } = useInitializeCoverages();

  const handleError = useErrorHandler();

  const saveCoverages = useSaveCoverages();

  useEffect(() => {
    getRateOnLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (shouldInitializeCoverages) {
      initializeCoverages()
        .then(() => refreshRate())
        .catch(() =>
          handleError({
            name: 'initializeCoveragesError',
            message: 'Something went wrong while initializing coverages.',
          })
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldInitializeCoverages]);

  if (!config) {
    return <LoadingPage />;
  }

  // Create a map of stacked coverages organized by their parent coverage
  const stackedCoverages = createStackedCoverageMap(
    config.coverages.insuranceProduct
  );

  return (
    <>
      <Body data-testid="coverages">
        {rewardsEnrollmentResponse?.isEnrolled && <RewardsBanner />}
        <ScreenHeader headerText="Coverages" />
        <Formik
          enableReinitialize
          initialValues={{
            coverages: buildCoveragesByFieldValue(details.coverages, 'value'),
          }}
          initialStatus={{
            healing: {
              coverages: getHealedCoverages(details.coverages),
            },
            errors: {
              coverages: getCoverageErrors(details.coverages),
            },
          }}
          onSubmit={(data, actions) => saveCoverages(data, actions)}
        >
          <CoveragesList
            config={mergeDictionaryIntoCoverages(
              config?.coverages,
              dictionary?.coverages
            )}
            details={details}
            onCoveragesPage
            stackedCoverages={stackedCoverages}
          />
        </Formik>
      </Body>
      <Footer
        nextButton={
          <FooterNextButton onClick={nextStep.onClickSuccess}>
            {nextStep.text}
          </FooterNextButton>
        }
        previousButton={
          <FooterPreviousButton onClick={previousStep.onClickSuccess}>
            {previousStep.text}
          </FooterPreviousButton>
        }
      />
    </>
  );
};
